/**
 * Transporte do arquivo até o armazenamento, com progresso honesto (TUT-169).
 *
 * ## Por que não é `fetch`
 *
 * O `fetch` **não reporta andamento de corpo ENVIADO**. Não é limitação da
 * nossa implementação: a API não tem esse evento (o `ReadableStream` de request
 * resolveria, mas não é suportado de forma utilizável nos navegadores que o
 * produto atende, e exige HTTP/2 no destino). O `XMLHttpRequest` tem
 * `upload.onprogress` desde sempre, e é essa a única razão de ele voltar aqui.
 *
 * ## O que "honesto" quer dizer, em código
 *
 * Só existe número quando o evento traz `lengthComputable`. Quando não traz —
 * e enquanto o navegador calcula o hash e a API autoriza —, o relato é de
 * FASE, sem número, e a barra desenha indeterminado. Animar de 0 a 100 sem
 * dado é o que a fatia recusou desde o desenho: barra falsa é pior que barra
 * nenhuma, porque mente sobre o que está acontecendo.
 *
 * ## O que NÃO mudou
 *
 * O caminho continua sendo o mesmo dos três envios do produto: o navegador
 * calcula o hash, a API autoriza, o arquivo vai direto ao armazenamento e a
 * API conclui. A API nunca transmite os bytes. Trocou-se o mensageiro do
 * segundo passo, não o desenho.
 */

export type ProgressoDeEnvio =
  /** Hash e autorização: trabalho real, sem quantidade que se possa afirmar. */
  | { fase: "preparando" }
  /** Transferência em curso, com bytes medidos pelo navegador. */
  | { fase: "enviando"; enviados: number; total: number }
  /** Bytes entregues; falta a API concluir e devolver o registro. */
  | { fase: "concluindo" };

export type RelatorDeProgresso = (progresso: ProgressoDeEnvio) => void;

/**
 * Devolve o status HTTP do armazenamento. Não interpreta: quem traduz recusa em
 * mensagem de usuário é o `api.ts`, que já tem a frase e o `ApiError`.
 */
export function enviarAoArmazenamento({
  url,
  method,
  headers,
  corpo,
  aoProgredir,
}: {
  url: string;
  method: string;
  headers: Record<string, string>;
  corpo: Blob;
  aoProgredir?: RelatorDeProgresso;
}): Promise<{ ok: boolean; status: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    for (const [nome, valor] of Object.entries(headers)) {
      xhr.setRequestHeader(nome, valor);
    }

    xhr.upload.addEventListener("progress", (evento) => {
      // Sem `lengthComputable` não há total; relatar 0 de 0 produziria uma
      // barra que salta para 100% e volta.
      if (!evento.lengthComputable) return;
      aoProgredir?.({
        fase: "enviando",
        enviados: evento.loaded,
        total: evento.total,
      });
    });

    // Bytes entregues ≠ operação concluída: falta a API fechar o envio. É essa
    // a diferença que a fase "concluindo" torna visível, em vez de deixar a
    // barra cheia e a tela parada sem explicação.
    xhr.upload.addEventListener("load", () => aoProgredir?.({ fase: "concluindo" }));

    xhr.addEventListener("load", () =>
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status }),
    );
    xhr.addEventListener("error", () =>
      reject(new Error("Falha de rede ao enviar o arquivo.")),
    );
    xhr.addEventListener("abort", () =>
      reject(new Error("Envio do arquivo cancelado.")),
    );

    xhr.send(corpo);
  });
}
